import { useState, useEffect } from "react";
import Spinner from "./Spinner";
import useEscapeKey from "../hooks/useEscapeKey";

export default function RegenerateImageModal({
  isOpen,
  onClose,
  segment,
  onRegenerate,
  isRegenerating = false,
}) {
  useEscapeKey(isOpen, onClose);
  const [prompt, setPrompt] = useState("");

  // Update prompt when segment changes (modal opens with new segment)
  useEffect(() => {
    if (segment?.prompt) {
      setPrompt(segment.prompt);
    } else {
      setPrompt("");
    }
  }, [segment]);

  if (!isOpen || !segment) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onRegenerate(segment.id, prompt.trim() || null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-lg sm:mx-4 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Regenerate Image</h3>
            <button
              onClick={onClose}
              disabled={isRegenerating}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4 space-y-4">
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={segment.image_url}
                alt="Current image"
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the image you want to generate..."
                rows={4}
                disabled={isRegenerating}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 resize-none"
              />
              <p className="mt-1 text-xs text-gray-400">
                Edit the prompt to change the generated image. Leave empty to use the existing prompt.
              </p>
            </div>

            {isRegenerating && (
              <div className="flex items-center gap-2 p-3 bg-primary-50 rounded-lg">
                <Spinner size="sm" />
                <span className="text-sm text-primary-700">Generating new image...</span>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isRegenerating}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isRegenerating}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isRegenerating ? (
                <>
                  <Spinner size="xs" color="text-white" />
                  Regenerating...
                </>
              ) : (
                <>
                  🔄 Regenerate
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
